import { useEffect, useRef } from "react";
import { useLocale } from "../contexts/LocaleContext";
import { PhotoEditor, type PhotoEditorRef } from "./PhotoEditor";
import "./CartPhotoEditorModal.css";

interface CartPhotoEditorModalProps {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onApply: (dataUrl: string, fileName: string) => void;
}

/**
 * Crop / zoom / pan / rotate editor matching legacy cart flow
 * (square viewport, JPEG export up to 1000²).
 */
export function CartPhotoEditorModal({
  open,
  file,
  onClose,
  onApply,
}: CartPhotoEditorModalProps) {
  const editorRef = useRef<PhotoEditorRef>(null);
  const { t } = useLocale();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const applyCrop = async () => {
    if (!file) return;
    const dataUrl = await editorRef.current?.exportCroppedJpeg();
    if (!dataUrl) return;
    onApply(dataUrl, file.name);
    onClose();
  };

  if (!open || !file) return null;

  return (
    <div
      className="cart-photo-editor-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-photo-editor-title"
    >
      <div className="cart-photo-editor-dialog">
        <div className="cart-photo-editor-header">
          <h3 id="cart-photo-editor-title">{t("cart.editImage")}</h3>
          <button
            type="button"
            className="cart-photo-editor-close"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="cart-photo-editor-body">
          <PhotoEditor ref={editorRef} source={file} />
        </div>
        <div className="cart-photo-editor-footer">
          <button
            type="button"
            className="cart-photo-editor-btn-secondary"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="cart-photo-editor-btn-primary"
            onClick={() => void applyCrop()}
          >
            {t("common.apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
